import { useEffect, useRef, useState } from 'react';
import aiSearchServices from '../../services/api-services/ai-search-api.service';
import AiResultCard from './AiResultCard';
import NoteText from './noteFormat';
import './aisearch.scss';

const NOTICE = 'AI notes are unavailable right now — results are ranked by keyword and semantic match.';

// PAGE_SIZE: how many results load by default and per scroll (what the user experiences).
// ANNOTATE_BATCH: entities per /annotate call — smaller calls return sooner, so the
// first complete cards paint while later batches are still being written.
export const PAGE_SIZE = 10;
export const ANNOTATE_BATCH = 5;

type Phase = 'idle' | 'loading' | 'annotating' | 'done' | 'degraded' | 'empty';

const AiResults = ({ query, searchId, onUnavailable, onMeta }: any) => {
    const { aiSearch, aiAnnotate } = aiSearchServices();
    const [results, setResults] = useState<any[]>([]);
    const [phase, setPhase] = useState<Phase>('idle');
    const [interpretation, setInterpretation] = useState('');
    const [notes, setNotes] = useState<Record<string, any>>({});
    // cards render as a contiguous prefix: a result shows only once its note (and
    // every higher-ranked result's note) has arrived — no half-finished cards
    const [annotatedUpTo, setAnnotatedUpTo] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const runRef = useRef(0);
    const moreInFlightRef = useRef(false);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const annotateWithRetry = async (q: string, ids: string[], run: number) => {
        let ann = await aiAnnotate(q, ids);
        if (run !== runRef.current) { return null; }
        if (!ann?.success) {
            // one retry before degrading: LLM calls fail transiently far more
            // often than permanently, and the notice is sticky for the search
            await new Promise((r) => setTimeout(r, 1500));
            if (run !== runRef.current) { return null; }
            ann = await aiAnnotate(q, ids);
            if (run !== runRef.current) { return null; }
        }
        return ann;
    };

    // Annotate items[from..to) as sequential ANNOTATE_BATCH-sized calls: one LLM
    // request in flight at a time is gentler on provider rate limits (concurrent
    // calls queue against each other and can all blow the client timeout), each
    // call gets the full timeout window, and cards stay in rank order for free.
    // Cards paint as each batch lands. Returns false if a batch failed after its
    // retry — later batches are then skipped (the notice is sticky anyway).
    const annotateRange = async (items: any[], from: number, to: number, run: number, q: string) => {
        for (let start = from; start < to; start += ANNOTATE_BATCH) {
            const end = Math.min(start + ANNOTATE_BATCH, to);
            const ids = items.slice(start, end).map((r: any) => r.entityId);
            const ann = await annotateWithRetry(q, ids, run);
            if (run !== runRef.current || !ann?.success) { return false; }
            setInterpretation((prev) => prev || ann.data?.interpretation || '');
            setNotes((prev) => {
                const next = { ...prev };
                for (const n of ann.data?.notes || []) { next[n.entityId] = n; }
                return next;
            });
            setAnnotatedUpTo((prev) => Math.max(prev, Math.min(end, items.length)));
        }
        return true;
    };

    useEffect(() => {
        if (!searchId || !query?.trim()) { return; }
        const run = ++runRef.current;
        const q = query.trim();
        moreInFlightRef.current = false;
        const go = async () => {
            setPhase('loading'); setResults([]); setNotes({}); setInterpretation('');
            setAnnotatedUpTo(0); setLoadingMore(false); onMeta?.(null);
            const res = await aiSearch(q);
            if (run !== runRef.current) { return; }
            if (!res?.success) { onUnavailable?.(res?.message); setPhase('idle'); return; }
            const items = res.data?.results || [];
            setResults(items);
            onMeta?.({
                count: items.length,
                members: res.data?.counts?.members ?? 0,
                facilities: res.data?.counts?.facilities ?? 0,
            });
            if (items.length === 0) { setPhase('empty'); return; }
            if (!res.data?.aiAvailable) { setPhase('degraded'); return; }
            setPhase('annotating');
            const ok = await annotateRange(items, 0, Math.min(PAGE_SIZE, items.length), run, q);
            if (run !== runRef.current) { return; }
            setPhase(ok ? 'done' : 'degraded');
        };
        go();
    }, [searchId]);

    // results beyond the visible pages annotate on demand: when the sentinel
    // scrolls into view, read the next PAGE_SIZE (as ANNOTATE_BATCH-sized calls)
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || phase !== 'done' || loadingMore) { return; }
        if (typeof IntersectionObserver === 'undefined') { return; }
        const run = runRef.current;
        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((e) => e.isIntersecting)) { return; }
            if (moreInFlightRef.current || run !== runRef.current) { return; }
            moreInFlightRef.current = true;
            observer.disconnect();
            setLoadingMore(true);
            const from = annotatedUpTo;
            const to = Math.min(from + PAGE_SIZE, results.length);
            annotateRange(results, from, to, run, query.trim()).then((ok) => {
                if (run !== runRef.current) { return; }
                moreInFlightRef.current = false;
                setLoadingMore(false);
                if (!ok) { setPhase('degraded'); }
            });
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [phase, annotatedUpTo, results, loadingMore]);

    if (phase === 'idle') { return null; }

    const q = query?.trim();
    const visibleCount = phase === 'degraded' ? results.length : annotatedUpTo;
    const showSkeletons = phase === 'loading' || (phase === 'annotating' && annotatedUpTo === 0);
    const remaining = results.length - visibleCount;

    return (
        <div className="ai-results">
            <div aria-live="polite">
                {phase === 'loading' && (
                    <div className="ai-searching">
                        <span className="spark" aria-hidden="true">✦</span>
                        <span className="msg"><b>Searching</b> members and facilities for “{q}”…</span>
                    </div>
                )}
                {phase === 'annotating' && annotatedUpTo === 0 && (
                    <div className="ai-searching">
                        <span className="spark" aria-hidden="true">✦</span>
                        <span className="msg"><b>Found {results.length} matches</b> · reading the top {Math.min(PAGE_SIZE, results.length)}…</span>
                    </div>
                )}
                {interpretation && annotatedUpTo > 0 && phase !== 'degraded' && (
                    <div className="ai-interp"><NoteText text={interpretation} /></div>
                )}
                {phase === 'degraded' && <div className="ai-notice">{NOTICE}</div>}
                {phase === 'empty' && (
                    <div className="ai-empty">
                        <b>No matches found</b>
                        Nothing in member CVs or facility descriptions matched this.
                        <p>Try dropping a constraint — a location or a specific instrument — or search the capability on its own.</p>
                    </div>
                )}
            </div>
            {showSkeletons && (
                <div aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className={`ai-cardskel${i === 1 ? ' fade2' : i === 2 ? ' fade3' : ''}`}>
                            <div className="av" />
                            <div className="col"><div className="bar" /><div className="bar" /><div className="bar" /></div>
                        </div>
                    ))}
                </div>
            )}
            {results.slice(0, visibleCount).map((r: any, i: number) => (
                <AiResultCard
                    key={`${r.entityType}:${r.entityId}`}
                    result={r}
                    note={notes[r.entityId] || null}
                    notesLoading={false}
                    index={i % ANNOTATE_BATCH}
                />
            ))}
            {phase === 'annotating' && annotatedUpTo > 0 && annotatedUpTo < Math.min(PAGE_SIZE, results.length) && (
                <div className="ai-more">
                    <span>Reading {Math.min(PAGE_SIZE, results.length) - annotatedUpTo} more…</span>
                </div>
            )}
            {phase === 'done' && remaining > 0 && (
                <div className="ai-more" ref={sentinelRef}>
                    <span className="down" aria-hidden="true">↓</span>
                    <span>
                        {loadingMore
                            ? `Reading ${Math.min(remaining, PAGE_SIZE)} more…`
                            : `${remaining} more matches — they’ll load as you scroll`}
                    </span>
                </div>
            )}
        </div>
    );
};

export default AiResults;
